import {
  ElementRef,
  FC,
  ForwardedRef,
  ReactNode,
  forwardRef,
  useState,
} from "react";
import AsyncSelect from "react-select/async";
import _uniqueId from "lodash/uniqueId";

import { getOptionsMaxHeight } from "./dropdown.utils";

export enum AsyncDropdownTestIds {
  Component = "async-dropdown",
}
import { Label, Asterisk, ErrorMessage } from "./Dropdown";
import {
  Control,
  DropdownContextProvider,
  DropdownIndicator,
  Group,
  GroupHeading,
  Input,
  Menu,
  MenuList,
  Option as OptionComponent,
  Placeholder,
  SingleValue,
} from "./components";
import { Option, DropdownProps, DropdownVariant } from "./types";
import { NoOptionsMessage } from "./components/NoOptionsMessage";
import { ClearIndicator } from "./components/ClearIndicator";
import { useDropdown } from "./useDropdown";

const overrideComponents = {
  ClearIndicator,
  Control,
  DropdownIndicator,
  Group,
  GroupHeading,
  IndicatorSeparator: () => null,
  Input,
  Menu,
  MenuList,
  NoOptionsMessage,
  Option: OptionComponent,
  Placeholder,
  SingleValue,
};

type AsyncDropdownContainerProps = {
  id?: string;
  testId?: string;
  label?: string;
  variant: DropdownVariant;
  isRequired: boolean;
  errorMessage: string;
  allowIcons: boolean;
  hasSelectedOption?: boolean;
  children: ReactNode;
  name: string;
  cachedOptions?: Option[];
};

export const AsyncDropdownContainer: FC<AsyncDropdownContainerProps> = ({
  id,
  name,
  testId,
  label,
  variant,
  isRequired,
  errorMessage,
  allowIcons,
  hasSelectedOption,
  children,
}) => {
  const hasError = errorMessage.length > 0;
  return (
    <div id={id} data-testid={testId ?? AsyncDropdownTestIds.Component}>
      <label htmlFor={name || id}>
        {label && (
          <Label
            $isDisabled={variant === "DISABLED" || variant === "READONLY"}
            $hasError={hasError}
          >
            {label} {isRequired ? <Asterisk>*</Asterisk> : null}
          </Label>
        )}
        <DropdownContextProvider
          allowIcons={allowIcons}
          hasError={hasError}
          variant={variant}
          hasSelectedOption={hasSelectedOption}
        >
          {children}
        </DropdownContextProvider>
      </label>
      {hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </div>
  );
};

type AsyncDropdownProps = DropdownProps & {
  getOptions: (searchValue: string) => Option[];
};

export const AsyncDropdown: FC<AsyncDropdownProps> = forwardRef(
  (
    {
      testId,
      label,
      variant = "DEFAULT",
      isRequired = false,
      errorMessage = "",
      allowIcons = false,
      options,
      defaultValue,
      value,
      name = "",
      onSelect,
      onClear,
      placeholder = "Select...",
      menuPlacement,
      maxOptionsToShow = 8,
      size = "NORMAL",
      getOptions,
      defaultOptions,
      id: customId,
      styles,
      ...selectProps
    },
    ref: ForwardedRef<ElementRef<typeof AsyncSelect>>
  ) => {
    const [id] = useState(_uniqueId("single-select-"));
    const { selected, setSelected, clearSelected, dropdownProps } = useDropdown(
      {
        defaultValue,
        value,
        name: name || id,
        onClear,
        options,
        placeholder,
        variant,
        menuPlacement,
        size,
        ...selectProps,
      }
    );

    const hasSelectedOption = !!selected;
    const loadOptions = async (inputValue: string) => getOptions(inputValue);

    return (
      <AsyncDropdownContainer
        id={customId}
        name={name}
        testId={testId}
        label={label}
        variant={variant}
        errorMessage={errorMessage}
        isRequired={isRequired}
        allowIcons={allowIcons}
        hasSelectedOption={hasSelectedOption}
      >
        <AsyncSelect
          {...dropdownProps}
          ref={ref}
          id={id}
          name={name}
          closeMenuOnSelect
          components={overrideComponents}
          cacheOptions={true}
          value={value}
          loadOptions={loadOptions}
          maxMenuHeight={getOptionsMaxHeight(maxOptionsToShow, defaultOptions)}
          onChange={(value, action) => {
            if (value && action.action === "select-option") {
              const option = value as Option;
              if (option !== selected) {
                setSelected(option);
                onSelect({ selected: option });
              }
            }
            if (onClear && action.action === "clear") {
              clearSelected();
              onClear({ cleared: action.removedValues?.[0] as Option });
            }
          }}
          styles={styles}
          {...(Boolean(defaultOptions) && { defaultOptions })}
        />
      </AsyncDropdownContainer>
    );
  }
);
